import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

function Login({ onLogin }) {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [wins, setWins] = useState([]);
  const [loadingWins, setLoadingWins] = useState(true);
  const [selectedImage, setSelectedImage] = useState(null);
  const [telegramLink, setTelegramLink] = useState('https://t.me/SPORTSPREDICTIONS_TIPS');
  const [showForgotPassword, setShowForgotPassword] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [showFpNewPwd, setShowFpNewPwd] = useState(false);
  const [showFpConfirm, setShowFpConfirm] = useState(false);
  const [fpStep, setFpStep] = useState('email'); // 'email' | 'code' | 'done'
  const [fpEmail, setFpEmail] = useState('');
  const [fpCode, setFpCode] = useState('');
  const [fpNewPassword, setFpNewPassword] = useState('');
  const [fpConfirm, setFpConfirm] = useState('');
  const [fpLoading, setFpLoading] = useState(false);
  const [fpMessage, setFpMessage] = useState('');
  const [fpError, setFpError] = useState('');
  const navigate = useNavigate();

  useEffect(() => {
    fetchWins();
    fetchSettings();
    trackVisit();
  }, []);

  const trackVisit = async () => {
    try {
      const fp = localStorage.getItem('visitor_fp') || `fp_${Date.now()}_${Math.random().toString(36).substr(2,9)}`;
      localStorage.setItem('visitor_fp', fp);
      await axios.post(`${API}/track-visit`, { fingerprint: fp });
    } catch (err) { console.error('Track visit error:', err); }
  };

  const fetchSettings = async () => {
    try {
      const response = await axios.get(`${API}/settings`);
      setTelegramLink(response.data.telegram_link);
    } catch (err) { console.error('Fetch settings error:', err); }
  };

  const fetchWins = async () => {
    try {
      const response = await axios.get(`${API}/wins`);
      setWins(response.data);
    } catch (err) { console.error('Fetch wins error:', err); }
    finally { setLoadingWins(false); }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      const res = await axios.post(`${API}/auth/login`, { email, password });
      localStorage.setItem('token', res.data.access_token);
      axios.defaults.headers.common['Authorization'] = `Bearer ${res.data.access_token}`;
      if (onLogin) onLogin(res.data.access_token);
      const userRes = await axios.get(`${API}/auth/me`);
      navigate(userRes.data.is_admin ? '/admin/dashboard' : '/dashboard');
    } catch (err) {
      setError(err.response?.data?.detail || 'Login failed. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleSendCode = async (e) => {
    e.preventDefault();
    setFpError(''); setFpMessage(''); setFpLoading(true);
    try {
      const res = await axios.post(`${API}/auth/forgot-password`, { email: fpEmail });
      setFpMessage(res.data.message);
      setFpStep('code');
    } catch (err) {
      setFpError(err.response?.data?.detail || 'Failed to send code');
    } finally { setFpLoading(false); }
  };

  const handleResetPassword = async (e) => {
    e.preventDefault();
    setFpError(''); setFpMessage('');
    if (fpNewPassword !== fpConfirm) { setFpError('Passwords do not match'); return; }
    if (fpNewPassword.length < 6) { setFpError('Password must be at least 6 characters'); return; }
    setFpLoading(true);
    try {
      const res = await axios.post(`${API}/auth/reset-password`, { email: fpEmail, code: fpCode, new_password: fpNewPassword });
      setFpMessage(res.data.message);
      setFpStep('done');
    } catch (err) {
      setFpError(err.response?.data?.detail || 'Failed to reset password');
    } finally { setFpLoading(false); }
  };

  const getWinImageUrl = (url) => {
    if (!url || url === 'None') return null;
    return url.startsWith('/') ? `${BACKEND_URL}${url}` : url;
  };

  return (
    <div className="min-h-screen bg-[#0a0e27]" data-testid="login-page">
      {/* Image Modal */}
      {selectedImage && (
        <div className="fixed inset-0 bg-black/90 z-50 flex items-center justify-center p-4" onClick={() => setSelectedImage(null)}>
          <button className="absolute top-4 right-4 text-white text-4xl font-bold hover:text-gray-300 z-50" onClick={() => setSelectedImage(null)}>&#10005;</button>
          <img src={selectedImage} alt="Win Screenshot" className="max-w-full max-h-[90vh] object-contain rounded-lg shadow-2xl" onClick={(e) => e.stopPropagation()} />
        </div>
      )}

      {/* Forgot Password Modal */}
      {showForgotPassword && (
        <div className="fixed inset-0 bg-black/80 z-50 flex items-center justify-center p-4" onClick={() => setShowForgotPassword(false)}>
          <div className="bg-[#0f1340] rounded-2xl p-6 w-full max-w-sm border border-white/10 shadow-2xl" onClick={(e) => e.stopPropagation()} data-testid="forgot-password-modal">
            <div className="flex items-center justify-between mb-5">
              <h3 className="text-lg font-extrabold text-white">
                {fpStep === 'email' ? 'Reset Password' : fpStep === 'code' ? 'Enter Code' : 'Success!'}
              </h3>
              <button onClick={() => setShowForgotPassword(false)} className="text-white/40 hover:text-white text-xl">&#10005;</button>
            </div>

            {fpError && <div className="bg-red-500/10 border border-red-500/30 text-red-400 px-3 py-2 rounded-lg mb-3 text-xs font-bold" data-testid="fp-error">{fpError}</div>}
            {fpMessage && <div className="bg-emerald-500/10 border border-emerald-500/30 text-emerald-400 px-3 py-2 rounded-lg mb-3 text-xs font-bold" data-testid="fp-message">{fpMessage}</div>}

            {fpStep === 'email' && (
              <form onSubmit={handleSendCode} className="space-y-3">
                <p className="text-white/40 text-xs mb-2">Enter your email and we will send you a 6-digit reset code.</p>
                <input type="email" value={fpEmail} onChange={(e) => setFpEmail(e.target.value)} required
                  className="w-full px-4 py-3 bg-white/5 border border-white/10 rounded-xl text-white placeholder-white/30 focus:ring-2 focus:ring-emerald-500 text-sm"
                  placeholder="your@email.com" data-testid="fp-email-input" />
                <button type="submit" disabled={fpLoading}
                  className="w-full py-3 bg-emerald-500 text-white font-extrabold rounded-xl hover:bg-emerald-600 transition-all disabled:opacity-50 text-sm"
                  data-testid="fp-send-code-button">
                  {fpLoading ? 'Sending...' : 'Send Reset Code'}
                </button>
              </form>
            )}

            {fpStep === 'code' && (
              <form onSubmit={handleResetPassword} className="space-y-3">
                <p className="text-white/40 text-xs mb-2">Check your email for the 6-digit code, then set your new password.</p>
                <input type="text" value={fpCode} onChange={(e) => setFpCode(e.target.value)} required maxLength={6}
                  className="w-full px-4 py-3 bg-white/5 border border-white/10 rounded-xl text-white placeholder-white/30 focus:ring-2 focus:ring-emerald-500 text-sm text-center tracking-[0.3em] font-bold text-lg"
                  placeholder="000000" data-testid="fp-code-input" />
                <div className="relative">
                  <input type={showFpNewPwd ? "text" : "password"} value={fpNewPassword} onChange={(e) => setFpNewPassword(e.target.value)} required
                    className="w-full px-4 py-3 pr-12 bg-white/5 border border-white/10 rounded-xl text-white placeholder-white/30 focus:ring-2 focus:ring-emerald-500 text-sm"
                    placeholder="New password (min 6 chars)" data-testid="fp-new-password-input" />
                  <button type="button" onClick={() => setShowFpNewPwd(!showFpNewPwd)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-white/40 hover:text-white/70 transition-colors" tabIndex={-1}>
                    {showFpNewPwd ? (
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13.875 18.825A10.05 10.05 0 0112 19c-4.478 0-8.268-2.943-9.543-7a9.97 9.97 0 011.563-3.029m5.858.908a3 3 0 114.243 4.243M9.878 9.878l4.242 4.242M9.878 9.878L3 3m6.878 6.878l4.242 4.242M21 21l-3.122-3.122" /></svg>
                    ) : (
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" /><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" /></svg>
                    )}
                  </button>
                </div>
                <div className="relative">
                  <input type={showFpConfirm ? "text" : "password"} value={fpConfirm} onChange={(e) => setFpConfirm(e.target.value)} required
                    className="w-full px-4 py-3 pr-12 bg-white/5 border border-white/10 rounded-xl text-white placeholder-white/30 focus:ring-2 focus:ring-emerald-500 text-sm"
                    placeholder="Confirm new password" data-testid="fp-confirm-input" />
                  <button type="button" onClick={() => setShowFpConfirm(!showFpConfirm)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-white/40 hover:text-white/70 transition-colors" tabIndex={-1}>
                    {showFpConfirm ? (
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13.875 18.825A10.05 10.05 0 0112 19c-4.478 0-8.268-2.943-9.543-7a9.97 9.97 0 011.563-3.029m5.858.908a3 3 0 114.243 4.243M9.878 9.878l4.242 4.242M9.878 9.878L3 3m6.878 6.878l4.242 4.242M21 21l-3.122-3.122" /></svg>
                    ) : (
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" /><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" /></svg>
                    )}
                  </button>
                </div>
                <button type="submit" disabled={fpLoading}
                  className="w-full py-3 bg-emerald-500 text-white font-extrabold rounded-xl hover:bg-emerald-600 transition-all disabled:opacity-50 text-sm"
                  data-testid="fp-reset-button">
                  {fpLoading ? 'Resetting...' : 'Reset Password'}
                </button>
                <button type="button" onClick={() => { setFpStep('email'); setFpError(''); setFpMessage(''); }}
                  className="w-full text-white/30 text-xs hover:text-white/50 transition-colors">
                  Resend code
                </button>
              </form>
            )}

            {fpStep === 'done' && (
              <div className="text-center">
                <div className="w-14 h-14 mx-auto mb-4 rounded-full bg-emerald-500/20 flex items-center justify-center">
                  <svg className="w-7 h-7 text-emerald-400" fill="currentColor" viewBox="0 0 20 20"><path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" /></svg>
                </div>
                <p className="text-white/50 text-sm mb-4">Your password has been reset. You can now login with your new password.</p>
                <button onClick={() => setShowForgotPassword(false)}
                  className="w-full py-3 bg-white text-[#0a0e27] font-extrabold rounded-xl hover:bg-white/90 transition-all text-sm"
                  data-testid="fp-back-to-login">
                  Back to Login
                </button>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Hero Section */}
      <div className="relative overflow-hidden">
        <div className="absolute inset-0">
          <img src="https://images.unsplash.com/photo-1608508136365-89375aa78e0c?w=1600&h=900&fit=crop" alt="Sports" className="w-full h-full object-cover" />
          <div className="absolute inset-0 bg-gradient-to-b from-[#0a0e27]/80 via-[#0a0e27]/90 to-[#0a0e27]"></div>
        </div>

        <div className="relative max-w-7xl mx-auto px-4 pt-8 pb-16">
          {/* Logo & Nav */}
          <div className="flex items-center justify-between mb-12">
            <div className="flex items-center gap-3">
              <img src="/logo.png" alt="Logo" className="h-12 w-12 object-contain" />
              <span className="text-white font-extrabold text-xl tracking-tight">Sports Predictions</span>
            </div>
            <div className="flex items-center gap-3">
              <a href={telegramLink} target="_blank" rel="noopener noreferrer" className="text-white/70 hover:text-white transition-colors">
                <svg className="w-6 h-6" fill="currentColor" viewBox="0 0 24 24">
                  <path d="M12 0C5.373 0 0 5.373 0 12s5.373 12 12 12 12-5.373 12-12S18.627 0 12 0zm5.894 8.221l-1.97 9.28c-.145.658-.537.818-1.084.508l-3-2.21-1.446 1.394c-.14.18-.357.295-.6.295l.213-3.054 5.56-5.022c.24-.213-.054-.334-.373-.121l-6.869 4.326-2.96-.924c-.64-.203-.658-.64.135-.954l11.566-4.458c.538-.196 1.006.128.832.941z"/>
                </svg>
              </a>
              <Link to="/signup" className="bg-emerald-500 hover:bg-emerald-600 text-white px-5 py-2 rounded-lg font-bold text-sm transition-all shadow-lg shadow-emerald-500/20" data-testid="signup-link">
                Sign Up
              </Link>
            </div>
          </div>

          {/* Hero Content */}
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div>
              {/* Discount Badge */}
              <div className="inline-flex items-center gap-2 bg-red-500/20 border border-red-500/40 text-red-400 px-4 py-2 rounded-full text-sm font-bold mb-6 animate-pulse" data-testid="discount-banner-login">
                <span className="w-2 h-2 bg-red-500 rounded-full"></span>
                LIMITED TIME: <span className="line-through text-white/40">$35</span> <span className="text-yellow-400 font-extrabold">$10/week</span> — SAVE 70%
              </div>

              <h1 className="text-4xl sm:text-5xl lg:text-6xl font-extrabold text-white leading-tight mb-6">
                Win Big With
                <span className="block bg-gradient-to-r from-emerald-400 to-cyan-400 bg-clip-text text-transparent">
                  Expert Picks
                </span>
              </h1>

              <p className="text-lg text-white/60 mb-8 max-w-lg">
                NBA, Soccer, MLB — daily premium predictions from analysts with a proven winning track record.
              </p>

              {/* Stats Row */}
              <div className="flex flex-wrap gap-6 mb-8">
                <div className="text-center">
                  <div className="text-3xl font-extrabold text-emerald-400">{wins.length}+</div>
                  <div className="text-xs text-white/50 font-bold uppercase tracking-wider">Verified Wins</div>
                </div>
                <div className="text-center">
                  <div className="text-3xl font-extrabold text-yellow-400">$26K+</div>
                  <div className="text-xs text-white/50 font-bold uppercase tracking-wider">Total Won</div>
                </div>
                <div className="text-center">
                  <div className="text-3xl font-extrabold text-cyan-400">Daily</div>
                  <div className="text-xs text-white/50 font-bold uppercase tracking-wider">New Picks</div>
                </div>
              </div>

              {/* Features */}
              <div className="space-y-3 mb-8">
                {[
                  'NBA, Soccer & MLB predictions daily',
                  'Parlay picks with high confidence',
                  'Instant access after subscribing',
                  'Cancel anytime — no commitment'
                ].map((feat) => (
                  <div key={feat} className="flex items-center gap-3">
                    <div className="w-5 h-5 bg-emerald-500/20 rounded-full flex items-center justify-center flex-shrink-0">
                      <svg className="w-3 h-3 text-emerald-400" fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                      </svg>
                    </div>
                    <span className="text-white/70 text-sm">{feat}</span>
                  </div>
                ))}
              </div>

              <Link to="/signup" className="inline-block bg-gradient-to-r from-emerald-500 to-cyan-500 hover:from-emerald-600 hover:to-cyan-600 text-white px-8 py-4 rounded-xl font-extrabold text-lg transition-all shadow-lg shadow-emerald-500/20 hover:shadow-emerald-500/40 hover:-translate-y-0.5">
                Start Winning — $10/week
              </Link>
            </div>

            {/* Login Form */}
            <div className="w-full max-w-md mx-auto lg:mx-0">
              <div className="bg-white/5 backdrop-blur-xl rounded-2xl p-8 border border-white/10 shadow-2xl" data-testid="login-form">
                <h2 className="text-xl font-extrabold text-white mb-6 text-center">Welcome Back</h2>

                {error && (
                  <div className="bg-red-500/10 border border-red-500/30 text-red-400 px-4 py-3 rounded-lg mb-4 text-sm font-bold" data-testid="login-error">
                    {error}
                  </div>
                )}

                <form onSubmit={handleSubmit} className="space-y-4">
                  <div>
                    <label className="block text-sm font-bold text-white/60 mb-2">Email</label>
                    <input
                      type="email" value={email} onChange={(e) => setEmail(e.target.value)}
                      className="w-full px-4 py-3 bg-white/5 border border-white/10 rounded-xl text-white placeholder-white/30 focus:ring-2 focus:ring-emerald-500 focus:border-transparent transition-all"
                      placeholder="your@email.com" required data-testid="login-email-input"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-bold text-white/60 mb-2">Password</label>
                    <div className="relative">
                      <input
                        type={showPassword ? "text" : "password"} value={password} onChange={(e) => setPassword(e.target.value)}
                        className="w-full px-4 py-3 pr-12 bg-white/5 border border-white/10 rounded-xl text-white placeholder-white/30 focus:ring-2 focus:ring-emerald-500 focus:border-transparent transition-all"
                        placeholder="Enter your password" required data-testid="login-password-input"
                      />
                      <button type="button" onClick={() => setShowPassword(!showPassword)}
                        className="absolute right-3 top-1/2 -translate-y-1/2 text-white/40 hover:text-white/70 transition-colors"
                        data-testid="login-password-toggle" tabIndex={-1}>
                        {showPassword ? (
                          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13.875 18.825A10.05 10.05 0 0112 19c-4.478 0-8.268-2.943-9.543-7a9.97 9.97 0 011.563-3.029m5.858.908a3 3 0 114.243 4.243M9.878 9.878l4.242 4.242M9.878 9.878L3 3m6.878 6.878l4.242 4.242M21 21l-3.122-3.122" /></svg>
                        ) : (
                          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" /><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" /></svg>
                        )}
                      </button>
                    </div>
                  </div>
                  <div className="flex justify-end">
                    <button type="button" onClick={() => { setShowForgotPassword(true); setFpStep('email'); setFpError(''); setFpMessage(''); setFpEmail(email); }}
                      className="text-emerald-400/70 hover:text-emerald-400 text-xs font-bold transition-colors" data-testid="forgot-password-link">
                      Forgot Password?
                    </button>
                  </div>
                  <button
                    type="submit" disabled={loading}
                    className="w-full bg-white text-[#0a0e27] py-3.5 rounded-xl font-extrabold hover:bg-white/90 transition-all disabled:opacity-50 shadow-lg"
                    data-testid="login-submit-button"
                  >
                    {loading ? (
                      <span className="flex items-center justify-center gap-2">
                        <span className="w-4 h-4 border-2 border-[#0a0e27] border-t-transparent rounded-full animate-spin"></span>
                        Signing in...
                      </span>
                    ) : 'Sign In'}
                  </button>
                </form>

                <p className="text-center text-white/40 mt-6 text-sm">
                  Don&apos;t have an account?{' '}
                  <Link to="/signup" className="text-emerald-400 hover:text-emerald-300 font-bold">Sign up</Link>
                </p>

                <div className="mt-6 pt-4 border-t border-white/10 flex justify-center gap-6">
                  <a href={telegramLink} target="_blank" rel="noopener noreferrer" className="text-white/40 hover:text-white/70 text-xs font-bold transition-colors">Telegram</a>
                  <a href="mailto:support@sportspredictionstips.com" className="text-white/40 hover:text-white/70 text-xs font-bold transition-colors">Support</a>
                  <Link to="/terms" className="text-white/40 hover:text-white/70 text-xs font-bold transition-colors">Terms</Link>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Wins Showcase */}
      <div className="max-w-7xl mx-auto px-4 py-16">
        <div className="text-center mb-10">
          <h2 className="text-3xl font-extrabold text-white mb-2">Proven Winning Track Record</h2>
          <p className="text-white/40">Real results from real bets — click any to view full screenshot</p>
        </div>

        {loadingWins ? (
          <div className="text-center text-white py-8">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-emerald-400 mx-auto"></div>
          </div>
        ) : wins.length === 0 ? (
          <div className="text-center text-white/40 py-8">
            <p>Winning results coming soon!</p>
          </div>
        ) : (
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {wins.map((win, index) => (
              <div
                key={win.id}
                className="group bg-white/5 backdrop-blur-sm rounded-2xl overflow-hidden border border-white/10 hover:border-emerald-500/30 transition-all duration-300 cursor-pointer hover:-translate-y-1"
                style={{ animation: 'fadeSlideUp 0.5s ease forwards', animationDelay: `${index * 0.1}s`, opacity: 0 }}
                onClick={() => setSelectedImage(getWinImageUrl(win.image_url))}
                data-testid={`login-win-card-${win.id}`}
              >
                {win.image_url && (
                  <div className="relative overflow-hidden">
                    <img
                      src={getWinImageUrl(win.image_url)}
                      alt={win.match_name}
                      className="w-full h-48 object-cover transition-transform duration-500 group-hover:scale-105"
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-[#0a0e27] via-transparent to-transparent"></div>
                    <div className="absolute bottom-3 left-4">
                      <span className="bg-emerald-500 text-white text-xs font-extrabold px-3 py-1 rounded-full shadow-lg">
                        {win.amount_won}
                      </span>
                    </div>
                  </div>
                )}
                <div className="p-4">
                  <h3 className="font-bold text-white text-sm mb-1">{win.match_name}</h3>
                  <span className="text-white/30 text-xs">{win.date}</span>
                </div>
              </div>
            ))}
          </div>
        )}

        {wins.length > 0 && (
          <div className="text-center mt-10">
            <Link to="/signup" className="inline-block bg-gradient-to-r from-emerald-500 to-cyan-500 hover:from-emerald-600 hover:to-cyan-600 text-white px-10 py-4 rounded-xl font-extrabold text-lg transition-all shadow-lg shadow-emerald-500/20 hover:-translate-y-0.5">
              Join Now & Start Winning
            </Link>
          </div>
        )}
      </div>
    </div>
  );
}

export default Login;
